import { Sparkles, RefreshCw } from 'lucide-react';
import type { AvatarMesh } from '@/services/avatarApi';
import { cn } from '@/lib/cn';

type CtaKind =
  | { kind: 'hidden' }
  | { kind: 'animate' }
  | { kind: 'inFlight' }
  | { kind: 'regenerate' }
  | { kind: 'retry' };

function decide(mesh?: AvatarMesh): CtaKind {
  if (!mesh) return { kind: 'hidden' };
  switch (mesh.status) {
    case 'ready':
      return mesh.animationsGlbUrl ? { kind: 'regenerate' } : { kind: 'animate' };
    case 'animating_queued':
    case 'animating_rigging':
    case 'animating_retargeting':
    case 'animating_merging':
    case 'animating_uploading':
      return { kind: 'inFlight' };
    case 'animated_ready':
      return { kind: 'regenerate' };
    case 'animated_failed':
      return { kind: 'retry' };
    default:
      return { kind: 'hidden' };
  }
}

interface AnimateMeCtaProps {
  mesh?: AvatarMesh;
  isPending: boolean;
  /**
   * Phase 3 — opens the library picker modal. The modal then triggers the
   * animate3d mutation with the chosen library + force flag.
   */
  onOpenModal: () => void;
}

export function AnimateMeCta({ mesh, isPending, onOpenModal }: AnimateMeCtaProps) {
  const cta = decide(mesh);
  if (cta.kind === 'hidden' || cta.kind === 'inFlight') return null;

  const baseClass =
    'flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity active:opacity-80';

  if (cta.kind === 'animate') {
    return (
      <button
        type="button"
        onClick={onOpenModal}
        disabled={isPending}
        className={cn(baseClass, 'bg-brand text-bg', isPending && 'opacity-60')}
      >
        <Sparkles aria-hidden className="h-4 w-4" />
        Animate me
      </button>
    );
  }
  if (cta.kind === 'regenerate') {
    return (
      <button
        type="button"
        onClick={onOpenModal}
        disabled={isPending}
        className={cn(baseClass, 'bg-bg-secondary text-fg', isPending && 'opacity-60')}
      >
        <RefreshCw aria-hidden className="h-4 w-4" />
        Animation packs
      </button>
    );
  }
  // retry
  return (
    <button
      type="button"
      onClick={onOpenModal}
      disabled={isPending}
      className={cn(baseClass, 'bg-danger/15 text-danger', isPending && 'opacity-60')}
    >
      <RefreshCw aria-hidden className="h-4 w-4" />
      Retry animations
    </button>
  );
}
